import { useState, useMemo } from 'react';
import styles from './LinkedMonthsRange.module.css';

export interface MonthYear {
  /** 1..12 */
  month: number;
  /** שנה מלאה (2026) */
  year: number;
}

export interface LinkedMonthsRangeProps {
  /** מערך של חודשים נבחרים */
  value: MonthYear[];
  onChange: (months: MonthYear[]) => void;
  /** מינימום בחירה (לא ניתן למחוק מתחת) */
  minMonths?: number;
  /** מקסימום בחירה (לא ניתן להוסיף מעבר) */
  maxMonths?: number;
  /** אם true — חורים מסומנים ב-warning */
  requireConsecutive?: boolean;
  /** הרשימה תתחיל מהחודש של maxDate (default: היום) */
  maxDate?: Date;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

const HEBREW_MONTHS: readonly string[] = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

const monthKey = (m: number, y: number): string => `${y}-${m}`;

const buildMonthList = (baseDate: Date, count: number): MonthYear[] => {
  const list: MonthYear[] = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(baseDate.getFullYear(), baseDate.getMonth() - i, 1);
    list.push({ month: d.getMonth() + 1, year: d.getFullYear() });
  }
  return list;
};

const isConsecutive = (months: MonthYear[]): boolean => {
  if (months.length <= 1) return true;
  const sorted = [...months].sort((a, b) => a.year - b.year || a.month - b.month);
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    const expectedMonth = prev.month === 12 ? 1 : prev.month + 1;
    const expectedYear = prev.month === 12 ? prev.year + 1 : prev.year;
    if (cur.month !== expectedMonth || cur.year !== expectedYear) return false;
  }
  return true;
};

/**
 * בחירת טווח חודשים רצופים — checkbox per month, "טען עוד" לחודשים נוספים.
 *
 * **Use case:** AddExpenseForm mode=`linkedRange` (R5) — חיוב פרוס על כמה חודשים.
 *
 * @example
 * <LinkedMonthsRange
 *   value={months}
 *   onChange={setMonths}
 *   minMonths={2}
 *   maxMonths={6}
 *   requireConsecutive
 * />
 */
export const LinkedMonthsRange = ({
  value,
  onChange,
  minMonths = 2,
  maxMonths = 12,
  requireConsecutive = true,
  maxDate,
  disabled = false,
  ariaLabel = 'בחירת טווח חודשים',
  className,
}: LinkedMonthsRangeProps): JSX.Element => {
  const [monthsToShow, setMonthsToShow] = useState(12);

  const baseDate = useMemo(() => maxDate || new Date(), [maxDate]);
  const monthList = useMemo(
    () => buildMonthList(baseDate, monthsToShow),
    [baseDate, monthsToShow]
  );

  const valueSet = useMemo<Set<string>>(
    () => new Set(value.map((v) => monthKey(v.month, v.year))),
    [value]
  );

  const consecutiveOK = !requireConsecutive || isConsecutive(value);
  const atMin = value.length <= minMonths;
  const atMax = value.length >= maxMonths;

  const handleToggle = (m: number, y: number): void => {
    if (disabled) return;
    const key = monthKey(m, y);
    if (valueSet.has(key)) {
      if (atMin) return;
      onChange(value.filter((v) => monthKey(v.month, v.year) !== key));
    } else {
      if (atMax) return;
      onChange([...value, { month: m, year: y }]);
    }
  };

  const loadMore = (): void => {
    if (!disabled) setMonthsToShow((n) => n + 12);
  };

  return (
    <div
      className={cx(styles.root, disabled && styles.disabled, className)}
      role="group"
      aria-label={ariaLabel}
      data-testid="linked-months-range"
    >
      <div className={styles.header}>
        <span className={styles.counter} data-testid="linked-months-range-counter">
          {`${value.length} מתוך ${maxMonths} חודשים נבחרו`}
        </span>
        {!consecutiveOK && (
          <span
            className={styles.warningBadge}
            role="alert"
            data-testid="linked-months-range-warning"
          >
            ⚠ יש פערים — נדרשת רציפות
          </span>
        )}
      </div>

      <ul className={styles.list}>
        {monthList.map((my) => {
          const key = monthKey(my.month, my.year);
          const isSelected = valueSet.has(key);
          const isFutureBlocked = (atMax && !isSelected) || (atMin && isSelected);
          return (
            <li key={key} className={styles.listItem}>
              <label
                className={cx(
                  styles.item,
                  isSelected && styles.itemSelected,
                  isFutureBlocked && styles.itemBlocked
                )}
                data-testid={`linked-months-range-item-${key}`}
              >
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={isSelected}
                  onChange={() => handleToggle(my.month, my.year)}
                  disabled={disabled || isFutureBlocked}
                  data-testid={`linked-months-range-checkbox-${key}`}
                  aria-label={`${HEBREW_MONTHS[my.month - 1]} ${my.year}`}
                />
                <span className={styles.label}>
                  <span className={styles.monthName}>{HEBREW_MONTHS[my.month - 1]}</span>
                  <span className={styles.year}>{my.year}</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        className={styles.loadMore}
        onClick={loadMore}
        disabled={disabled}
        data-testid="linked-months-range-load-more"
      >
        טען 12 חודשים נוספים אחורה
      </button>

      <div className={styles.constraints} data-testid="linked-months-range-constraints">
        מינ׳ {minMonths} · מקס׳ {maxMonths}{' '}
        {requireConsecutive && '· רציף בלבד'}
      </div>
    </div>
  );
};
